import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined'
import { Alert, Input, Select, Space } from 'antd'
import {
  EnrichedOntologyFieldDefinition,
  FIELD_TYPE_DISPLAY_LABELS,
  OntologyFieldType,
  useOntology,
} from 'features/dynamicOntology'
import { FC, useMemo } from 'react'

interface PropertySelectionStepProps {
  selectedType: string
  isWithinEntryCountLimit: boolean
  isEditingCustomProperty: boolean
  selectedField: string
  customPropertyName: string
  customPropertyType: OntologyFieldType
  onPropertySelectionChange: (value: string) => void
  onCustomPropertyNameChange: (value: string) => void
  onCustomPropertyTypeChange: (value: OntologyFieldType) => void
}

const convertFieldsToOptions = (
  fields: Record<string, EnrichedOntologyFieldDefinition>,
  selectedType: string
) =>
  Object.entries(fields)
    .filter(([fieldKey]) => fieldKey.startsWith(`${selectedType}.`))
    .filter(([fieldKey]) => !fieldKey.endsWith('.id')) // Disallow editing 'id' fields
    .map(([fieldKey, fieldDef]) => ({
      value: fieldKey,
      label: fieldDef.name,
    }))
    .toSorted((a, b) => a.label.localeCompare(b.label))

export const PropertySelectionStep: FC<PropertySelectionStepProps> = ({
  selectedType,
  isWithinEntryCountLimit,
  isEditingCustomProperty,
  selectedField,
  customPropertyName,
  customPropertyType,
  onPropertySelectionChange,
  onCustomPropertyNameChange,
  onCustomPropertyTypeChange,
}) => {
  const { fields } = useOntology()

  const fieldOptions = useMemo(() => {
    if (!selectedType) return []

    return convertFieldsToOptions(fields, selectedType)
  }, [fields, selectedType])

  const hasPropertyError =
    Boolean(selectedType) &&
    isWithinEntryCountLimit &&
    !(
      Boolean(selectedField) ||
      (isEditingCustomProperty && Boolean(customPropertyName))
    )

  return (
    <Space orientation='vertical' style={{ width: '100%' }}>
      <Select
        value={isEditingCustomProperty ? 'custom' : selectedField}
        onChange={onPropertySelectionChange}
        placeholder='Choose a property or create custom'
        style={{ width: '100%' }}
        status={hasPropertyError ? 'error' : undefined}
        getPopupContainer={(triggerNode) =>
          triggerNode.parentNode as HTMLElement
        }
        options={[
          ...fieldOptions,
          { value: 'custom', label: '+ Create Custom Property' },
        ]}
      />
      {isEditingCustomProperty && (
        <Space>
          <Input
            placeholder='Property name'
            value={customPropertyName}
            onChange={(e) => onCustomPropertyNameChange(e.target.value)}
            style={{ width: 200 }}
            status={hasPropertyError ? 'error' : undefined}
          />
          <Select
            value={customPropertyType}
            onChange={(value) =>
              onCustomPropertyTypeChange(value as OntologyFieldType)
            }
            options={[...FIELD_TYPE_DISPLAY_LABELS]}
            style={{ width: 120 }}
            getPopupContainer={(triggerNode) =>
              triggerNode.parentNode as HTMLElement
            }
          />
        </Space>
      )}
      {hasPropertyError && (
        <Alert
          title='Please select a property or enter a custom property name'
          type='error'
          showIcon
          icon={<ExclamationCircleOutlined />}
        />
      )}
    </Space>
  )
}
